/**
 * Event definitions (R9) — what the scheduler fires.
 *
 * @remarks
 * An event is a condition-gated effect bundle with a WHEN supplied from
 * outside: nothing here says when it fires, because arming is an EFFECT
 * (`schedule_event`, carrying the delay) and time is the turn counter. The
 * definition only says what happens.
 *
 * **Deliberately effects-only** — no `narratives` field, ruled with the R9
 * plan sign-off (Mike, 2026-08-21). An interaction's `narratives` are the
 * world's RESPONSE to an actor; an event has no actor — it is the world
 * happening — and `append_narrative` is already exactly its voice. One
 * ordered list also buys what a narratives-then-effects pair cannot express:
 * a line, a flag flip, then another line, interleaved however the fiction
 * wants. A purely mechanical event (a door failing open three turns after
 * the mains die) is legal with no line at all.
 *
 * `conditions` are re-checked AT FIRE TIME and a failing event drops
 * silently: scheduling captures an intention, and the world may have moved.
 * The audit trail tells fired from dropped (`firedEventIds` vs
 * `processedEventIds`), so a replay can pin the difference.
 *
 * Repetition is SELF-SCHEDULING — an event whose effects `schedule_event`
 * itself is the repeating event. No repeat flag, no cadence field: the
 * composition, again.
 *
 * **`resolvesAt` (D188) — when, inside its due turn, the occurrence fires.**
 * The turn runs action → scheduler tick → triggers, so by default a due event
 * fires BEFORE the turn's watchers look, and a deadline resolves before the
 * act that would have beaten it is judged: a player who closes the door on
 * the turn the gas kills dies with the door in hand. Mike's ruling
 * (2026-09-17): the act wins that tie. An event declaring
 * `resolvesAt: 'end_of_turn'` is held past the triggers and fires last in
 * its turn — so a trigger that cancels it, or makes its conditions false,
 * on the very turn it is due, has beaten it. Everything else is unchanged:
 * the due turn, the countdown, the audit trail. The default, `'tick'`, is
 * the order every pack has always had.
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { effectSchema } from './effects.js';
import { eventDefinitionIdSchema } from './identifiers.js';

/**
 * Where in its due turn an occurrence fires: at the scheduler tick (before
 * the turn's triggers — the default), or at the end of the turn, after the
 * triggers have judged what the action did (D188).
 */
export const eventResolutionSchema = z.enum(['tick', 'end_of_turn']);

/** See {@link eventResolutionSchema}. */
export type EventResolution = z.infer<typeof eventResolutionSchema>;

/** One authored event — see the module remarks. */
export const eventDefinitionSchema = z
	.strictObject({
		conditions: z.array(conditionSchema).readonly().default([]),
		effects: z.array(effectSchema).min(1).readonly(),
		eventId: eventDefinitionIdSchema,
		resolvesAt: eventResolutionSchema.default('tick'),
	})
	.readonly();

/** One authored event — see {@link eventDefinitionSchema}. */
export type EventDefinition = z.infer<typeof eventDefinitionSchema>;
